import { useState } from 'react';
import { callLimitsApi, type CallLimitsView } from '../../api/client';
import { Button, Card, Chip, Field, Notice } from '../../components/ui';
import { fullWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → Customers → a company → Calls abroad (Billing phase 2a, 3186): whether the company
 * may call abroad and premium-rate numbers (off for new companies until staff turn it on), its daily
 * and monthly limits, what it has spent today and this month, and whether Stitch has paused those
 * calls on the phone system. "Let calls through" lifts a limit pause until a time, with a reason.
 */

const dollars = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toCents = (v: string): number | null => {
  const n = Number(v.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

export function CallsAbroadCard({ customerId, hasTenant }: { customerId: string; hasTenant: boolean }) {
  const data = useData(() => callLimitsApi.view(customerId), [customerId], { live: false });
  const [daily, setDaily] = useState<string | null>(null);
  const [monthly, setMonthly] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v = data.data;
  const l = v?.limits;

  async function run(fn: () => Promise<CallLimitsView>, ok: string) {
    setBusy(true);
    setSaid(null);
    try {
      await fn();
      data.reload();
      setSaid({ tone: 'ok', text: ok });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  }

  const overriding = Boolean(l?.overrideUntil && Date.parse(l.overrideUntil) > Date.now());
  const chip = !l ? undefined : !l.international ? (
    <Chip tone="neutral">Off</Chip>
  ) : l.paused === 'daily' || l.paused === 'monthly' ? (
    <Chip tone="crit">{l.paused === 'daily' ? 'Paused: daily limit' : 'Paused: monthly limit'}</Chip>
  ) : overriding ? (
    <Chip tone="warn">Let through</Chip>
  ) : (
    <Chip tone="ok">On</Chip>
  );
  const endOfDay = () => {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d.toISOString();
  };

  return (
    <Card title="Calls abroad" action={chip}>
      <div className="card-body section-stack">
        <p className="muted small">
          Calls to other countries (the Caribbean included) and to premium-rate numbers (900, 976) cost money a minute, so they’re off for new companies until turned on here, and
          paused when a company reaches its daily or monthly limit. Calls in the US and Canada are never affected.
        </p>
        {l && (
          <label className="check">
            <input
              type="checkbox"
              checked={l.international}
              disabled={busy}
              onChange={(e) => {
                const on = e.target.checked;
                void run(() => callLimitsApi.save(customerId, { international: on }), on ? 'Turned on. Their extensions get their own dial plans back now.' : 'Turned off. Their extensions are paused now.');
              }}
            />
            <span>
              Allowed to call abroad and premium-rate numbers
              <span className="hint"> Within the limits below. Turning it off pauses those calls on the phone system straight away.</span>
            </span>
          </label>
        )}
        {l && v && (
          <dl className="facts">
            <dt>Today so far</dt>
            <dd>{v.spend ? `${dollars(v.spend.todayCents)} of ${dollars(l.dailyCents)}` : 'Not known: the rating service isn’t answering'}</dd>
            <dt>This month so far</dt>
            <dd>{v.spend ? `${dollars(v.spend.monthCents)} of ${dollars(l.monthlyCents)}` : 'Not known'}</dd>
            {v.spend && v.spend.unratedMinutes > 0 && (
              <>
                <dt>No rate yet</dt>
                <dd>{`${v.spend.unratedMinutes.toLocaleString('en-US')} minutes (counted at $1 a minute here; add their rate under System → Calls abroad)`}</dd>
              </>
            )}
            <dt>On the phone system</dt>
            <dd>
              {l.paused
                ? `Paused since ${l.pausedAt ? fullWhen(l.pausedAt) : 'now'} (${Object.keys(l.savedPlans).length} extensions on the pause plan)`
                : 'Not paused'}
            </dd>
            {overriding && (
              <>
                <dt>Let through</dt>
                <dd>{`Until ${fullWhen(l.overrideUntil!)}, by ${l.overrideBy ?? 'Stitch staff'}${l.overrideReason ? `: ${l.overrideReason}` : ''}`}</dd>
              </>
            )}
          </dl>
        )}
        {!hasTenant && <Notice tone="warn">Not linked to a tenant yet: nothing to pause.</Notice>}
        {l?.problem && <Notice tone="crit">{l.problem}</Notice>}
        {v && !v.settings.pausePlan && <Notice tone="warn">No dial plan is chosen for pausing calls abroad yet: choose it in System → Calls abroad.</Notice>}
        {l && (
          <form
            className="row-actions"
            onSubmit={(e) => {
              e.preventDefault();
              const d = daily === null ? l.dailyCents : toCents(daily);
              const m = monthly === null ? l.monthlyCents : toCents(monthly);
              if (d === null || m === null) {
                setSaid({ tone: 'crit', text: 'Enter each limit in dollars, like 50 or 250.' });
                return;
              }
              void run(async () => {
                const r = await callLimitsApi.save(customerId, { dailyCents: d, monthlyCents: m });
                setDaily(null);
                setMonthly(null);
                return r;
              }, 'Limits saved.');
            }}
          >
            <Field label="Daily limit ($)" inputMode="decimal" value={daily ?? (l.dailyCents / 100).toFixed(2)} onChange={(e) => setDaily(e.target.value)} />
            <Field label="Monthly limit ($)" inputMode="decimal" value={monthly ?? (l.monthlyCents / 100).toFixed(2)} onChange={(e) => setMonthly(e.target.value)} />
            <Button type="submit" disabled={busy || (daily === null && monthly === null)}>
              Save limits
            </Button>
          </form>
        )}
        {l?.international && (
          <div className="stack">
            {overriding ? (
              <div className="row-actions">
                <Button variant="ghost" disabled={busy} onClick={() => void run(() => callLimitsApi.override(customerId, null), 'The limits apply again.')}>
                  Stop letting calls through
                </Button>
              </div>
            ) : (
              <>
                <Field label="Why let calls through past the limits" value={reason} onChange={(e) => setReason(e.target.value)} hint="Kept in the activity log. For calls the company expects; raise the limit if it’s for good." />
                <div className="row-actions">
                  <Button disabled={busy || !reason.trim()} onClick={() => void run(() => callLimitsApi.override(customerId, endOfDay(), reason), 'Calls abroad go through until the end of today.')}>
                    Let calls through until tonight
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy || !reason.trim()}
                    onClick={() => void run(() => callLimitsApi.override(customerId, new Date(Date.now() + 7 * 86_400_000).toISOString(), reason), 'Calls abroad go through for 7 days.')}
                  >
                    For 7 days
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
      </div>
    </Card>
  );
}
